"use client";

import { useSyncExternalStore } from "react";

// ----------------------------------------------------------------------
// "Reveal the header" signal: true once the page is past a given offset AND the
// reader is heading back up. Built on useSyncExternalStore like useMediaQuery —
// no effect, no set-state-in-effect — over a single shared scroll listener.
// ----------------------------------------------------------------------

/** Swallows the few pixels a collapsing mobile URL bar emits, which would
 *  otherwise read as a direction change and flicker the header. */
const DIRECTION_THRESHOLD = 8;

/* Starts "down" so nothing is revealed until the reader actually scrolls up —
   a reload restored mid-page must not pop the header open on its own. */
let scrollY = 0;
let isScrollingUp = false;
let lastY = 0;
let frame: number | null = null;
const listeners = new Set<() => void>();

function read() {
  frame = null;
  const y = Math.max(window.scrollY, 0);
  const delta = y - lastY;

  if (Math.abs(delta) >= DIRECTION_THRESHOLD) {
    isScrollingUp = delta < 0;
    lastY = y;
  }

  if (y === scrollY) return;
  scrollY = y;
  listeners.forEach((notify) => notify());
}

function onScroll() {
  if (frame !== null) return;
  frame = requestAnimationFrame(read);
}

function subscribe(callback: () => void) {
  if (listeners.size === 0) {
    // A reload can restore the page mid-scroll, so start from where we are.
    scrollY = Math.max(window.scrollY, 0);
    lastY = scrollY;
    isScrollingUp = false;
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  listeners.add(callback);

  return () => {
    listeners.delete(callback);
    if (listeners.size === 0) {
      window.removeEventListener("scroll", onScroll);
      if (frame !== null) {
        cancelAnimationFrame(frame);
        frame = null;
      }
    }
  };
}

/**
 * @param afterPx Offset the page has to be past before anything is revealed —
 *                above it the real header is still on screen.
 *
 * Returns a boolean rather than the raw offset on purpose: consumers then only
 * re-render when the answer flips, not on every scroll frame.
 */
export default function useScrollReveal(afterPx = 0): boolean {
  return useSyncExternalStore(
    subscribe,
    () => scrollY > afterPx && isScrollingUp,
    () => false, // SSR/pre-hydration snapshot
  );
}
